/** What the hub's notification and approval rows do (spec §5 “Where each row goes”). */
import { useCallback } from "react";
import { getSubscriptionStatus } from "@/api/billing";
import type { NotificationRowData, PendingApproval } from "@/api/notifications";
import {
  openFrameInProject,
  openTaskInProject,
} from "@/components/layout/cross-project-navigation";
import { useLayout } from "@/components/layout/layout-provider";
import { openProjectMemory } from "@/components/shapes/memory-frame/open-project-memory";
import { openConversationChat } from "@/components/toasts/work-toast-actions";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { selectFramesByType } from "@/stores/frames-selectors";
import { useFramesStore } from "@/stores/frames-store";
import { useNotificationStore } from "@/stores/notification-store";

/**
 * The memory frame in Focus, on the thing when the row names one. A project
 * without the frame gets one only with the knowledge graph; without it the
 * row opens Settings › Memory rather than making a Pro-only frame.
 */
function openMemoryTarget(nodeId: string | null, openSettings: () => void) {
  if (selectFramesByType(useFramesStore.getState(), "memory").length > 0) {
    openProjectMemory(nodeId);
    return;
  }
  const scope = captureDesktopChatAuthScopeGuard();
  void getSubscriptionStatus()
    .then((status) => status.has_pro_access)
    .catch(() => false)
    .then((pro) => {
      if (!scope()) return;
      if (pro) openProjectMemory(nodeId);
      else openSettings();
    });
}

export function useNotificationActions() {
  const layout = useLayout();

  const showTask = useCallback(
    (taskId: string, projectId: string | null, projectName: string | null) => {
      void openTaskInProject({ id: taskId, projectId, projectName });
    },
    [],
  );

  /** Opens the row's target and marks it read; false when the row has nowhere to go. */
  const openRow = useCallback(
    (row: NotificationRowData): boolean => {
      const target = row.target;
      if (!target) return false;
      useNotificationStore.getState().readRow(row);
      switch (target.type) {
        case "task":
          if (!target.id) return false;
          showTask(target.id, row.project_id, row.project_name);
          return true;
        case "frame":
          if (!target.id) return false;
          void openFrameInProject({
            id: target.id,
            projectId: row.project_id,
            projectName: row.project_name,
          });
          return true;
        case "conversation":
          if (!target.id) return false;
          void openConversationChat(target.id, `notification-${row.id}`);
          return true;
        case "memory":
          openMemoryTarget(target.id, () =>
            layout.openSettings({ page: "memory" }),
          );
          return true;
        case "release_notes":
          return true;
      }
    },
    [layout, showTask],
  );

  /** An approval opens the task and the chat that asked. */
  const openApproval = useCallback(
    (approval: PendingApproval) => {
      showTask(approval.task_id, approval.project_id, approval.project_name);
      void openConversationChat(
        approval.conversation_id,
        `approval-${approval.id}`,
      );
    },
    [showTask],
  );

  const clearRow = useCallback(
    (row: NotificationRowData) => useNotificationStore.getState().clearRow(row),
    [],
  );
  const decide = useCallback(
    (approval: PendingApproval, allow: boolean) =>
      void useNotificationStore.getState().decideApproval(approval, allow),
    [],
  );
  const markAllRead = useCallback(
    () => useNotificationStore.getState().markAllRead(),
    [],
  );
  const clearAll = useCallback(
    () => useNotificationStore.getState().clearAll(),
    [],
  );

  return { openRow, openApproval, clearRow, decide, markAllRead, clearAll };
}
